/**
 * Coach-facing copy for the two numbers behind one check — the one the coach
 * SAID out loud, and the one their own demo SHOWED (PR-7 of the coach-input
 * refactor, `docs/coach_input/REFACTOR_PLAN.md` §C.3b(3)).
 *
 * The 2026-05-15 locked decision §3: **what you said beats what your body
 * did.** A coach who says "lead elbow straight, 170 degrees" is stating a
 * target; the 163° their demo happened to produce is not that target. Until
 * PR-7 the stated number never reached scoring at all, so every athlete was
 * quietly judged against the demo (audit items TV-3 / FE-1 / E2E-3).
 *
 * SINGLE SOURCE of every string the suggestion card renders about those two
 * numbers, so the copy gate in `docs/COACH_FACING_COPY_STANDARD.md` §4 has one
 * file to review and the component stays layout-only. Same shape as
 * `gateCopy.ts`, which owns the OTHER number on the same check.
 *
 * WHAT / WHY / HOW, across the surface (standard §1):
 *   · WHAT — `judgedAgainstLine()`: which number this check will be judged
 *     against, with the number in it.
 *   · WHY  — the backend's `verbal_target_conflict` notice, rendered VERBATIM
 *     through the ordinary notice renderer. Nothing here writes a why.
 *   · HOW  — `switchActionLabel()`: the one action, naming the number the
 *     coach would be switching to.
 *
 * WHAT THIS MODULE NEVER DOES: decide whether the two numbers disagree, or
 * which one is in force. The BACKEND measures the demonstrated value, sets
 * `conflict`, and resolves `priority`; this module reads all three. A second,
 * independent opinion about the same thing is the class of bug the refactor
 * exists to remove.
 */

import type { NoticeKind, VerbalTarget } from '@/types/api';

/**
 * The notice that explains a conflict, by kind.
 *
 * This is the ONE place the UI branches on a `NoticeKind`, and the one place
 * the name is written. The card needs THIS note about the check and not the
 * others it may also carry (a phase it could not match, a standard strictness),
 * so joining on `source_ref` alone is not enough here. Typed as `NoticeKind`
 * so a rename on the backend half of the contract fails to compile rather than
 * silently matching nothing.
 */
export const VERBAL_TARGET_CONFLICT_KIND: NoticeKind = 'verbal_target_conflict';

// ---------------------------------------------------------------------------
// Degrees, as a coach writes them
// ---------------------------------------------------------------------------

/**
 * One angle, in WHOLE degrees: `170` → `"170°"`, `163.44` → `"163°"`.
 *
 * Whole degrees because a coach says "about one-seventy", never "one hundred
 * and sixty-three point four four" — and a decimal here would read as a
 * precision the camera does not have (standard §2: "numbers only when the
 * coach can act on them"). The SCORER keeps the full value; only this display
 * rounds.
 */
export function formatDegrees(value: number): string {
  return `${Math.round(value)}°`;
}

/** `null` for anything that is not a real number, so callers render nothing. */
function finiteOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * The number the coach stated: a single angle as `"170°"`, a range as
 * `"165–175°"` (one degree symbol, en dash — the way a coach writes a range).
 *
 * Returns `null` when neither end was stated, and treats a range whose ends
 * round to the same whole degree as the single number it reads as.
 */
export function formatStatedDegrees(
  statedMin: number | null | undefined,
  statedMax: number | null | undefined,
): string | null {
  const min = finiteOrNull(statedMin);
  const max = finiteOrNull(statedMax);
  if (min === null && max === null) return null;
  if (min === null) return formatDegrees(max as number);
  if (max === null) return formatDegrees(min);
  if (Math.round(min) === Math.round(max)) return formatDegrees(min);
  return `${Math.round(min)}–${formatDegrees(max)}`;
}

/** The stated number for a whole `VerbalTarget`, or `null` if none was stated. */
export function statedDegrees(target: VerbalTarget): string | null {
  return formatStatedDegrees(target.stated_min, target.stated_max);
}

/** The demo's measured number, or `null` when it could not be measured. */
export function demonstratedDegrees(target: VerbalTarget): string | null {
  const value = finiteOrNull(target.demonstrated_value);
  return value === null ? null : formatDegrees(value);
}

// ---------------------------------------------------------------------------
// Does the card say anything at all?
// ---------------------------------------------------------------------------

/**
 * Whether to show the conflict row: the backend called it a conflict AND there
 * is a measured number to compare against.
 *
 * Both halves are required by plan §C.3b(3) — "the badge renders only when a
 * `measured_value` exists to compare against". A conflict with nothing to
 * compare would offer the coach a switch to a number we do not have.
 */
export function hasVerbalTargetConflict(target: VerbalTarget): boolean {
  return target.conflict === true && finiteOrNull(target.demonstrated_value) !== null;
}

/**
 * Whether to offer the switch at all.
 *
 * A conflict is the ordinary reason. The second reason exists so a coach can
 * never be STRANDED: once they have switched to the demo, the way back is
 * offered whether or not the backend still calls the two numbers a conflict.
 * Both numbers must exist either way — the label names the one being switched
 * to.
 *
 * (Why the second clause is not redundant: `conflict` is the backend's verdict
 * that the two numbers DISAGREE, which a switch does not change — so it should
 * stay `true` after one. But "should" is the other half of a contract built in
 * parallel, and the cost of being wrong about it is a coach locked into their
 * demo's number with no way back. `priority === 'demonstrated'` with two real
 * numbers only ever happens because the coach chose it.)
 */
export function offersVerbalTargetSwitch(target: VerbalTarget): boolean {
  if (hasVerbalTargetConflict(target)) return true;
  return (
    target.priority === 'demonstrated' &&
    statedDegrees(target) !== null &&
    demonstratedDegrees(target) !== null
  );
}

/**
 * Whether to show the quiet agreement line: no conflict, BOTH numbers exist,
 * and the check is judged against what the coach said. An unmeasurable demo
 * says nothing — not agreement — and "matches your demo" means nothing on a
 * check that is being judged against the demo.
 */
export function showsMatchesDemo(target: VerbalTarget): boolean {
  if (target.conflict === true) return false;
  if (target.priority !== 'verbal') return false;
  return statedDegrees(target) !== null && demonstratedDegrees(target) !== null;
}

/** The quiet line for a check whose stated number and demo agree. */
export const MATCHES_YOUR_DEMO_SENTENCE = 'Matches your demo';

// ---------------------------------------------------------------------------
// Which number is in force — the WHAT
// ---------------------------------------------------------------------------

/**
 * One line naming the number this check will be judged against.
 *
 * Driven by `priority`, which the backend resolved. Returns `null` when the
 * number that priority points at is missing, so the card shows nothing rather
 * than an empty promise.
 */
export function judgedAgainstLine(target: VerbalTarget): string | null {
  if (target.priority === 'demonstrated') {
    const demo = demonstratedDegrees(target);
    return demo === null ? null : `Judged against your demo (${demo})`;
  }
  const stated = statedDegrees(target);
  return stated === null ? null : `Judged against the number you said (${stated})`;
}

// ---------------------------------------------------------------------------
// The one action — the HOW
// ---------------------------------------------------------------------------

/**
 * The switch, named by the number the coach would be switching TO.
 *
 * Judged against what they said → offer the demo's number. Already judged
 * against the demo → offer their own number back. Returns `null` when the
 * number on the other side is missing, so no card offers a switch to nothing.
 */
export function switchActionLabel(target: VerbalTarget): string | null {
  if (target.priority === 'demonstrated') {
    const stated = statedDegrees(target);
    return stated === null ? null : `Switch back to the ${stated} you said`;
  }
  const demo = demonstratedDegrees(target);
  return demo === null ? null : `Switch to the ${demo} in your demo`;
}

/** While the check is being rebuilt against the other number. */
export const SWITCH_PENDING_LABEL = 'Switching…';

/**
 * What `verbal_target_priority` must say for this goal to flip.
 *
 * `true` = the number the coach said wins (the default); `false` = score
 * against the demo. Derived from the CURRENT `priority` the backend reported,
 * so the client never holds its own idea of which number is in force.
 */
export function nextVerbalTargetPriority(target: VerbalTarget): boolean {
  return target.priority !== 'verbal';
}
